import { useEffect, useRef } from "react";
import { ELEMENT_TYPES } from "./ElementLine";

/**
 * Final-Draft-style type picker that appears below an empty line.
 *
 *  - Keyboard: digits 1-8 pick a type; Esc cancels; any letter writes that letter
 *    in the line (handled by parent via onTypeAny).
 *  - Mouse: click on any chip.
 *  - Auto-dismisses if user clicks outside.
 */
export default function TypePicker({ onPick, onCancel }) {
  const boxRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel?.();
        return;
      }
      // Number shortcut
      const n = parseInt(e.key, 10);
      if (!isNaN(n) && n >= 1 && n <= ELEMENT_TYPES.length) {
        e.preventDefault();
        onPick?.(ELEMENT_TYPES[n - 1].id);
        return;
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onPick, onCancel]);

  // Click-outside to dismiss
  useEffect(() => {
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        onCancel?.();
      }
    };
    // Defer one tick so the same click that opened it doesn't immediately close it
    const id = setTimeout(() => document.addEventListener("mousedown", onDown), 50);
    return () => {
      clearTimeout(id);
      document.removeEventListener("mousedown", onDown);
    };
  }, [onCancel]);

  return (
    <div
      ref={boxRef}
      data-testid="sp-type-picker"
      className="my-2 ml-8 inline-flex flex-wrap items-center gap-1.5 rounded-lg border border-[#26775E]/40 bg-white px-3 py-2 shadow-md"
    >
      <span className="mr-1 text-[10px] uppercase tracking-widest text-stone-500">
        ¿Qué viene?
      </span>
      {ELEMENT_TYPES.map((t) => (
        <button
          key={t.id}
          onMouseDown={(e) => {
            e.preventDefault();
            onPick?.(t.id);
          }}
          data-testid={`sp-type-pick-${t.id}`}
          className="inline-flex items-center gap-1 rounded-full border border-stone-300 bg-white px-2.5 py-0.5 text-[11px] font-mono uppercase tracking-wider text-stone-700 transition hover:border-[#26775E] hover:bg-[#26775E] hover:text-white"
        >
          <span className="font-bold text-[#26775E] group-hover:text-white">{t.shortcut}</span>
          <span>{t.label}</span>
        </button>
      ))}
      <button
        onMouseDown={(e) => {
          e.preventDefault();
          onCancel?.();
        }}
        data-testid="sp-type-pick-cancel"
        title="Cancelar (Esc)"
        className="ml-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest text-stone-400 hover:text-red-600"
      >
        Esc
      </button>
    </div>
  );
}
